import React, { useMemo, useState } from 'react'

import {
    Box,
    Chip,
    IconButton,
    InputAdornment,
    Modal,
    Paper,
    Slide,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import {
    Close as CloseIcon,
    Search as SearchIcon,
    Event as EventIcon,
    LocationOn as LocationIcon,
    Schedule as TimeIcon,
} from '@material-ui/icons'

import { DateTime } from 'luxon'
import { PropertyEvent } from '../../models/PropertyEvent'

const BORDER = '#e6e9ed'

type TimeFilter = 'all' | 'upcoming' | 'past'

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        borderRadius: 16,
        overflow: 'hidden',
        outline: 'none',
        boxShadow:
            '0 24px 48px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.04)',
        width: '90vw',
        [theme.breakpoints.up('sm')]: {
            width: '80vw',
            maxWidth: 720,
        },
        height: '80vh',
        [theme.breakpoints.down('xs')]: {
            height: '92vh',
        },
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${BORDER}`,
        backgroundColor: '#fff',
    },
    headerIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${theme.palette.primary.main}14`,
        color: theme.palette.primary.main,
        flexShrink: 0,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 3),
        borderBottom: `1px solid ${BORDER}`,
        backgroundColor: '#fff',
        flexWrap: 'wrap',
    },
    searchField: {
        flex: 1,
        minWidth: 180,
    },
    filterChips: {
        display: 'flex',
        gap: theme.spacing(0.75),
        flexShrink: 0,
    },
    filterChip: {
        fontWeight: 600,
        fontSize: '0.75rem',
        height: 28,
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'all 0.15s',
    },
    filterChipActive: {
        backgroundColor: `${theme.palette.primary.main}14`,
        color: theme.palette.primary.main,
        border: `1.5px solid ${theme.palette.primary.main}`,
    },
    filterChipInactive: {
        backgroundColor: '#fff',
        color: theme.palette.text.secondary,
        border: `1.5px solid ${BORDER}`,
        '&:hover': {
            borderColor: '#b0b8c1',
        },
    },
    body: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        backgroundColor: '#f9fafb',
    },
    dateGroup: {
        padding: theme.spacing(0, 2.5),
        '&:first-child': {
            paddingTop: theme.spacing(2),
        },
    },
    dateLabel: {
        fontSize: '0.75rem',
        fontWeight: 700,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        padding: theme.spacing(1, 0.5),
        position: 'sticky',
        top: 0,
        backgroundColor: '#f9fafb',
        zIndex: 1,
    },
    eventCard: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        marginBottom: theme.spacing(1),
        backgroundColor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        cursor: 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': {
            borderColor: theme.palette.primary.light,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        },
    },
    eventDot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        flexShrink: 0,
    },
    eventDotPublished: {
        backgroundColor: theme.palette.primary.main,
    },
    eventDotDraft: {
        backgroundColor: 'transparent',
        border: `2.5px solid ${theme.palette.grey[500]}`,
    },
    eventInfo: {
        flex: 1,
        minWidth: 0,
    },
    eventTitle: {
        fontWeight: 600,
        fontSize: '0.9rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    eventMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginTop: 2,
    },
    eventMetaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    statusChip: {
        height: 20,
        fontSize: '0.65rem',
        fontWeight: 700,
        flexShrink: 0,
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(6),
        color: theme.palette.text.secondary,
    },
    resultCount: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        padding: theme.spacing(1.5, 3, 0),
        fontWeight: 500,
    },
}))

interface EventListModalProps {
    open: boolean
    onClose: () => void
    events: PropertyEvent[]
    onEventClick: (event: PropertyEvent) => void
    timezone: string
}

interface GroupedEvents {
    label: string
    sortKey: string
    events: PropertyEvent[]
}

export const EventListModal: React.FC<EventListModalProps> = ({
    open,
    onClose,
    events,
    onEventClick,
    timezone,
}) => {
    const classes = useStyles()
    const [search, setSearch] = useState('')
    const [timeFilter, setTimeFilter] = useState<TimeFilter>('upcoming')

    const now = useMemo(() => DateTime.now().setZone(timezone), [timezone])

    const filteredEvents = useMemo(() => {
        let result = events

        if (timeFilter === 'upcoming') {
            result = result.filter(
                (e) => DateTime.fromISO(e.starts_at).setZone(timezone) >= now,
            )
        } else if (timeFilter === 'past') {
            result = result.filter(
                (e) => DateTime.fromISO(e.starts_at).setZone(timezone) < now,
            )
        }

        if (search.trim()) {
            const term = search.toLowerCase()
            result = result.filter(
                (e) =>
                    e.title.toLowerCase().includes(term) ||
                    e.location.toLowerCase().includes(term) ||
                    e.description.toLowerCase().includes(term),
            )
        }

        return result
    }, [events, search, timeFilter, now, timezone])

    const grouped = useMemo<GroupedEvents[]>(() => {
        const groups: Record<string, PropertyEvent[]> = {}
        const sorted = [...filteredEvents].sort((a, b) => {
            const diff =
                new Date(a.starts_at).getTime() -
                new Date(b.starts_at).getTime()
            return timeFilter === 'past' ? -diff : diff
        })

        sorted.forEach((event) => {
            const dt = DateTime.fromISO(event.starts_at).setZone(timezone)
            const key = dt.toFormat('yyyy-MM-dd')
            if (!groups[key]) {
                groups[key] = []
            }
            groups[key].push(event)
        })

        const entries = Object.entries(groups)
        if (timeFilter === 'past') {
            entries.sort(([a], [b]) => b.localeCompare(a))
        }

        const today = DateTime.now().setZone(timezone)

        return entries.map(([key, evts]) => {
            const dt = DateTime.fromISO(evts[0].starts_at).setZone(timezone)
            const isToday = dt.hasSame(today, 'day')
            const isTomorrow = dt.hasSame(today.plus({ days: 1 }), 'day')
            const isYesterday = dt.hasSame(today.minus({ days: 1 }), 'day')

            let label = dt.toFormat('EEEE, MMMM d, yyyy')
            if (isToday) label = `Today — ${dt.toFormat('MMMM d')}`
            if (isTomorrow) label = `Tomorrow — ${dt.toFormat('MMMM d')}`
            if (isYesterday) label = `Yesterday — ${dt.toFormat('MMMM d')}`

            return { label, sortKey: key, events: evts }
        })
    }, [filteredEvents, timeFilter, timezone])

    const upcomingCount = useMemo(
        () =>
            events.filter(
                (e) => DateTime.fromISO(e.starts_at).setZone(timezone) >= now,
            ).length,
        [events, now, timezone],
    )
    const pastCount = useMemo(
        () =>
            events.filter(
                (e) => DateTime.fromISO(e.starts_at).setZone(timezone) < now,
            ).length,
        [events, now, timezone],
    )

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper className={classes.paper}>
                    {/* Header */}
                    <Box className={classes.header}>
                        <Box className={classes.headerIcon}>
                            <EventIcon />
                        </Box>
                        <Box flex={1}>
                            <Typography
                                style={{
                                    fontWeight: 700,
                                    fontSize: '1.05rem',
                                    lineHeight: 1.2,
                                }}
                            >
                                All Events
                            </Typography>
                            <Typography
                                style={{
                                    fontSize: '0.8rem',
                                    color: '#6b7280',
                                }}
                            >
                                {events.length} event
                                {events.length !== 1 ? 's' : ''} in current view
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Toolbar: Search + Filters */}
                    <Box className={classes.toolbar}>
                        <TextField
                            className={classes.searchField}
                            placeholder="Search events..."
                            variant="outlined"
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon
                                            style={{
                                                fontSize: 20,
                                                color: '#9ca3af',
                                            }}
                                        />
                                    </InputAdornment>
                                ),
                                style: { borderRadius: 8 },
                            }}
                        />
                        <Box className={classes.filterChips}>
                            <Chip
                                label={`Upcoming (${upcomingCount})`}
                                size="small"
                                className={`${classes.filterChip} ${
                                    timeFilter === 'upcoming'
                                        ? classes.filterChipActive
                                        : classes.filterChipInactive
                                }`}
                                onClick={() => setTimeFilter('upcoming')}
                                variant="outlined"
                            />
                            <Chip
                                label={`Past (${pastCount})`}
                                size="small"
                                className={`${classes.filterChip} ${
                                    timeFilter === 'past'
                                        ? classes.filterChipActive
                                        : classes.filterChipInactive
                                }`}
                                onClick={() => setTimeFilter('past')}
                                variant="outlined"
                            />
                            <Chip
                                label="All"
                                size="small"
                                className={`${classes.filterChip} ${
                                    timeFilter === 'all'
                                        ? classes.filterChipActive
                                        : classes.filterChipInactive
                                }`}
                                onClick={() => setTimeFilter('all')}
                                variant="outlined"
                            />
                        </Box>
                    </Box>

                    {/* Result count */}
                    {filteredEvents.length > 0 && (
                        <Typography className={classes.resultCount}>
                            Showing {filteredEvents.length} event
                            {filteredEvents.length !== 1 ? 's' : ''}
                        </Typography>
                    )}

                    {/* Event List */}
                    <Box className={classes.body}>
                        {grouped.length === 0 ? (
                            <Box className={classes.emptyState}>
                                <EventIcon
                                    style={{
                                        fontSize: 48,
                                        marginBottom: 8,
                                        opacity: 0.3,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    style={{ fontWeight: 500 }}
                                >
                                    {search
                                        ? 'No events match your search'
                                        : timeFilter === 'upcoming'
                                        ? 'No upcoming events'
                                        : timeFilter === 'past'
                                        ? 'No past events'
                                        : 'No events scheduled'}
                                </Typography>
                            </Box>
                        ) : (
                            grouped.map((group) => (
                                <Box
                                    key={group.sortKey}
                                    className={classes.dateGroup}
                                >
                                    <Typography className={classes.dateLabel}>
                                        {group.label}
                                    </Typography>
                                    {group.events.map((event) => {
                                        const isDraft = event.status === 'DRAFT'
                                        const startDt = DateTime.fromISO(
                                            event.starts_at,
                                        ).setZone(timezone)
                                        const endDt = DateTime.fromISO(
                                            event.ends_at,
                                        ).setZone(timezone)

                                        const timeStr = event.is_all_day
                                            ? 'All day'
                                            : `${startDt.toFormat(
                                                  'h:mm a',
                                              )} – ${endDt.toFormat('h:mm a')}`

                                        return (
                                            <Box
                                                key={event.id}
                                                className={classes.eventCard}
                                                onClick={() =>
                                                    onEventClick(event)
                                                }
                                            >
                                                <span
                                                    className={`${
                                                        classes.eventDot
                                                    } ${
                                                        isDraft
                                                            ? classes.eventDotDraft
                                                            : classes.eventDotPublished
                                                    }`}
                                                />
                                                <Box
                                                    className={
                                                        classes.eventInfo
                                                    }
                                                >
                                                    <Typography
                                                        className={
                                                            classes.eventTitle
                                                        }
                                                    >
                                                        {event.title}
                                                    </Typography>
                                                    <Box
                                                        className={
                                                            classes.eventMeta
                                                        }
                                                    >
                                                        <span
                                                            className={
                                                                classes.eventMetaItem
                                                            }
                                                        >
                                                            <TimeIcon
                                                                style={{
                                                                    fontSize: 14,
                                                                }}
                                                            />
                                                            {timeStr}
                                                        </span>
                                                        {event.location && (
                                                            <span
                                                                className={
                                                                    classes.eventMetaItem
                                                                }
                                                            >
                                                                <LocationIcon
                                                                    style={{
                                                                        fontSize: 14,
                                                                    }}
                                                                />
                                                                {event.location}
                                                            </span>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Chip
                                                    label={
                                                        isDraft
                                                            ? 'Draft'
                                                            : 'Published'
                                                    }
                                                    size="small"
                                                    className={
                                                        classes.statusChip
                                                    }
                                                    style={{
                                                        backgroundColor: isDraft
                                                            ? '#f3f4f6'
                                                            : '#ecfdf5',
                                                        color: isDraft
                                                            ? '#6b7280'
                                                            : '#059669',
                                                    }}
                                                />
                                            </Box>
                                        )
                                    })}
                                </Box>
                            ))
                        )}
                        <Box style={{ height: 16 }} />
                    </Box>
                </Paper>
            </Slide>
        </Modal>
    )
}
